import React from 'react';
import { X, Edit, Calendar, Clock } from 'lucide-react';
import type { AccountExecutive } from '../../../../../types/accountExecutiveMaintenance';
import './AccountExecutiveDetailsModal.css';

interface AccountExecutiveDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountExecutive: AccountExecutive | null;
  onEdit?: (accountExecutive: AccountExecutive) => void;
}

const AccountExecutiveDetailsModal: React.FC<AccountExecutiveDetailsModalProps> = ({
  isOpen,
  onClose,
  accountExecutive,
  onEdit
}) => {
  if (!isOpen || !accountExecutive) {
    return null;
  }

  const handleEdit = () => {
    if (onEdit) {
      onEdit(accountExecutive);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="account-executive-modal-overlay" onClick={onClose}>
      <div className="account-executive-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="account-executive-modal-header">
          <div className="account-executive-modal-header-left">
            <div className="account-executive-modal-icon">
              <div className={`status-dot ${accountExecutive.isActive ? 'active' : 'inactive'}`}></div>
            </div>
            <div className="account-executive-modal-title-section">
              <h2 className="account-executive-modal-title">Account Executive Details</h2>
              <div className="account-executive-modal-subtitle">
                {accountExecutive.aeCode} - {accountExecutive.firstName} {accountExecutive.lastName}
              </div>
            </div>
          </div>
          <div className="account-executive-modal-header-actions">
            <button
              className="account-executive-modal-edit-btn"
              onClick={handleEdit}
              title="Edit Account Executive"
            >
              <Edit size={16} />
              Edit
            </button>
            <button
              className="account-executive-modal-close-btn"
              onClick={onClose}
              title="Close Modal"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="account-executive-modal-content">
          <div className="account-executive-details-grid">
            {/* Basic Information */}
            <div className="account-executive-detail-section">
              <h3 className="account-executive-detail-section-title">Basic Information</h3>
              <div className="account-executive-detail-grid">
                <div className="account-executive-detail-item">
                  <label className="account-executive-detail-label">AE Code</label>
                  <div className="account-executive-detail-value ae-code">{accountExecutive.aeCode}</div>
                </div>
                <div className="account-executive-detail-item">
                  <label className="account-executive-detail-label">Status</label>
                  <div className="account-executive-detail-value">
                    <div className={`status-indicator ${accountExecutive.isActive ? 'active' : 'inactive'}`}>
                      <div className={`status-dot ${accountExecutive.isActive ? 'active' : 'inactive'}`}></div>
                      {accountExecutive.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
                <div className="account-executive-detail-item">
                  <label className="account-executive-detail-label">First Name</label>
                  <div className="account-executive-detail-value">{accountExecutive.firstName}</div>
                </div>
                <div className="account-executive-detail-item">
                  <label className="account-executive-detail-label">Middle Name</label>
                  <div className="account-executive-detail-value">{accountExecutive.middleName || '-'}</div>
                </div>
                <div className="account-executive-detail-item">
                  <label className="account-executive-detail-label">Last Name</label>
                  <div className="account-executive-detail-value">{accountExecutive.lastName || '-'}</div>
                </div>
              </div>
            </div>

            {/* System Information */}
            <div className="account-executive-detail-section">
              <h3 className="account-executive-detail-section-title">System Information</h3>
              <div className="account-executive-detail-grid">
                <div className="account-executive-detail-item">
                  <label className="account-executive-detail-label">
                    <Calendar size={14} />
                    Created Date
                  </label>
                  <div className="account-executive-detail-value">{formatDate(accountExecutive.createdAt)}</div>
                </div>
                {accountExecutive.lastModified && (
                  <div className="account-executive-detail-item">
                    <label className="account-executive-detail-label">
                      <Clock size={14} />
                      Last Modified
                    </label>
                    <div className="account-executive-detail-value">{formatDate(accountExecutive.lastModified)}</div>
                  </div>
                )}
                <div className="account-executive-detail-item">
                  <label className="account-executive-detail-label">Record ID</label>
                  <div className="account-executive-detail-value system-id">{accountExecutive.id}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="account-executive-modal-footer">
          <div className="account-executive-modal-footer-info">
            <span className="account-executive-modal-footer-text">
              Account Executive record for {accountExecutive.aeCode}
            </span>
          </div>
          <div className="account-executive-modal-footer-actions">
            <button
              className="account-executive-modal-btn secondary"
              onClick={onClose}
            >
              Close
            </button>
            <button
              className="account-executive-modal-btn primary"
              onClick={handleEdit}
            >
              <Edit size={16} />
              Edit Account Executive
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountExecutiveDetailsModal;